/**
 * Loadout invariants — the single post-apply predicate that answers *"is this
 * resulting mech loadout legal?"*. The seam the three equip reducers
 * (`equip_weapon`/`equip_system`/`attach_mod`) call **after** building their
 * `nextMech`, hard-rejecting on a non-null violation. One pure function, five
 * same-shaped tenants — the same rules-live-once-in-the-wall discipline as `sp.ts` /
 * `licenses.ts` / `pilotGear.ts`, and the place a future declarative / third-party
 * loadout-constraint loader would plug in (deferred until a pack demands a rule the
 * seam can't name).
 *
 * **Why core TypeScript, not the atom/effects DSL**: the
 * atom system is a *combat-time* effect resolver (its context is encounter
 * `Combatant`s, not loadouts); the build-time tag-constraint domain is tiny and
 * uniform (essentially Unique + AI), so a two-tenant DSL is over-engineering. This is
 * the deliberately-flat home for it.
 *
 * The five tenants, checked in order (first violation wins):
 *   - **SP** — `spUsed > spMax` (`computeSpMax`/`computeSpUsed` from `sp.ts`, so the
 *     gate reads the exact number `compute.ts` displays). This is the single source of
 *     the SP check `applyEquipSystem`/`applyAttachMod` enforce.
 *   - **Unique** — any `tg_unique` item id appearing ≥2× *within its own surface*
 *     (weapon id across mount slots; system id in the `systems` list; mod id across
 *     slots). Per-mech, RAW-parity (matches COMP/CON's per-loadout enforcement).
 *   - **AI** — more than one **system** carrying `tg_ai` (per-mech). Systems-only per
 *     the shipped tag data; weapons/mods are NOT swept for `tg_ai` (a deliberate scope
 *     call — one core mod carries `tg_ai` and is intentionally ignored).
 *   - **Superheavy bracing** — a Superheavy weapon (size `Superheavy`) "require[s] both
 *     a HEAVY mount and one other mount of any size" (core rulebook p.32), the second
 *     left empty as bracing. So a loadout with N Superheavy weapons needs N *other*
 *     fully-empty mounts. Purely derived (no brace marker stored): the Heavy-slot size
 *     gate (`weaponFitsSlot`, `mounts.ts`) already places it; this enforces the cost.
 *     RAW has no adjacency requirement (COMP/CON's "adjacent mount" is a stricter UI
 *     nicety) — any empty mount counts as the brace, and the tenant composes both ways:
 *     equipping the Superheavy with no spare mount rejects, and so does filling the last
 *     spare mount while a Superheavy is equipped (that would consume the brace).
 *   - **Flex mount exclusivity** — a Flexible mount takes "either one MAIN weapon or up to two AUXILIARY
 *     weapons" (core p.32), a PAIR constraint the per-slot `SLOT_FITTINGS` table can't
 *     express on its own (it would also admit a Main in slot 0 alongside an Auxiliary
 *     in slot 1 — RAW forbids that combination). A Main-sized weapon can only ever land
 *     in a Flex mount's slot 0 — slot 1's fitting is plain Auxiliary, so `weaponFitsSlot`
 *     already rejects a Main there — so the tenant only has to check slot 0's occupant
 *     against slot 1's. Hard-rejects and NAMES the fix (unequip one or the other); it
 *     never silently drops the player's weapon — the shape COMP/CON's dynamic `Slots`
 *     getter gets wrong, silently unequipping the player's second Aux as a side effect
 *     of a read.
 *
 * **All five tenants gather their weapon/mod ids off `composedMounts(loadout, pilot,
 * compendium)`** (`composedMounts.ts`), never `loadout.mounts` directly — a weapon/mod
 * equipped in a core-bonus bonus mount (e.g. Improved Armament's Flex mount) is a real
 * mech-wide SP cost / Unique surface / Superheavy brace / Flex-exclusivity candidate, so
 * it must be visible here exactly like a native-mount one. Reading `loadout.mounts`
 * instead lets a Flex-mounted mod dodge the SP budget and a `tg_unique` weapon be
 * duplicated one-native/one-Flex.
 */
import type { MechData, MountData, PilotData } from "./types";
import { computeSpMax, computeSpUsed } from "./sp";
import { composedMounts, composedMountProvenance, type CoreBonusCompendium } from "./composedMounts";

/** The Lancer **Unique** item tag id (`@massif/lancer-data` `tags.json`). */
export const TG_UNIQUE = "tg_unique";
/** The Lancer **AI** item tag id (`@massif/lancer-data` `tags.json`). */
export const TG_AI = "tg_ai";
/**
 * The Lancer **No Cascade** HIDDEN TAG id (`@massif/lancer-data` `tags.json`) — RAW: "Prohibits
 * an AI system from being recognized as a valid target for Cascading." Per-SYSTEM exemption:
 * a system carrying BOTH `tg_ai` and this tag (a comp/con-class
 * assistant unit) is self-exempt and contributes no cascade risk, matching the demand text's
 * "an AI system" grain rather than a per-mech flag. See `@lancer-vtt/rules`' `tables.ts` for the
 * cascade-check consumer.
 */
export const TG_NO_CASCADE = "tg_no_cascade";
/** The weapon size string a Superheavy weapon declares in its `mount` field. */
export const WEAPON_SIZE_SUPERHEAVY = "Superheavy";

/**
 * Is this weapon profile barred from a Skirmish (and so from Overwatch, RAW's "SKIRMISH as a
 * reaction")? A Superheavy is (Core p.70: "can only be fired as part of a BARRAGE"), unless the
 * profile carries lancer-data's `skirmish: true`: the Leviathan Heavy Assault Cannon's Standard
 * profile (Core p.134, "Unlike other SUPERHEAVY weapons, the Leviathan can be used with
 * SKIRMISH"). An alternate profile carries no `mount`, so pass the base weapon's `mount` for it.
 */
export function superheavyBarsSkirmish(weapon: { mount?: string; skirmish?: true }, mount: string | undefined = weapon.mount): boolean {
  return mount === WEAPON_SIZE_SUPERHEAVY && weapon.skirmish !== true;
}
/** The weapon size string an Auxiliary weapon declares in its `mount` field (the Barrage aux-rider gate). */
export const WEAPON_SIZE_AUXILIARY = "Auxiliary";
/** The weapon size string a Main weapon declares in its `mount` field (the Flex-exclusivity tenant). */
export const WEAPON_SIZE_MAIN = "Main";

/**
 * A structured loadout-rule violation. `kind` lets a driver branch (e.g. distinct
 * UI styling per tenant); `message` is a ready-to-surface human reason the reducer
 * wraps into a `CommandError` and the kit affordance can render.
 */
export type LoadoutViolation = {
  kind: "sp" | "unique" | "ai" | "superheavy" | "flex";
  message: string;
};

/** The item shape the tag-reading tenants need: a list of embedded tag refs. */
type TaggedItem = { tags?: { id: string }[] };

/**
 * The read-only compendium slice the invariant needs: resolve a weapon/system/mod to
 * its `sp` (for the SP tenant — superset of {@link SpCompendium}), its embedded `tags`
 * (for the Unique/AI tenants), and its `mount` size string (for the Superheavy tenant —
 * weapons only), a frame to its `sp` stat, and (via {@link CoreBonusCompendium}) a core
 * bonus by id to its `bonuses` rows — the Unique/SP/Superheavy tenants all gather their
 * ids off `composedMounts(loadout, pilot, compendium)`, which needs
 * that tenant to resolve bonus-mount inclusion. A structural slice (mirrors `sp.ts`'s
 * `SpCompendium` / `licenses.ts`'s `LicenseCompendium`) so the domain package leans on
 * the compendium's *types* only — the real `Compendium` satisfies it (its items carry
 * `sp`, `tags`, and `mount`).
 */
export type LoadoutInvariantCompendium = CoreBonusCompendium & {
  getById(type: "frame", id: string): { stats?: { sp?: number } } | undefined;
  getById(
    type: "weapon" | "system" | "mod",
    id: string,
  ): (TaggedItem & { sp?: number; mount?: string }) | undefined;
};

/** Whether a resolved compendium item carries the given tag id. */
function hasTag(item: TaggedItem | undefined, tagId: string): boolean {
  return item?.tags?.some((t) => t.id === tagId) ?? false;
}

/**
 * The first id in `ids` that both **repeats** within the list and carries `tg_unique`,
 * or `null` if none. The duplicate is detected at its 2nd occurrence (≥2× within the
 * surface). An id that doesn't resolve against the compendium carries no tag and so
 * never trips — the derive-then-skip discipline of the gear/core-bonus gates.
 */
function firstUniqueDuplicate(
  ids: readonly string[],
  type: "weapon" | "system" | "mod",
  compendium: LoadoutInvariantCompendium,
): string | null {
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id) && hasTag(compendium.getById(type, id), TG_UNIQUE)) return id;
    seen.add(id);
  }
  return null;
}

/**
 * All loadout violations on a **resulting** mech (post-apply — build `nextMech` first,
 * then call this), in tenant order (`sp`,
 * `unique`, `ai`, `superheavy`, `flex`). Unlike {@link checkLoadoutInvariants},
 * **no tenant shields a later one from running** — every tenant is evaluated against
 * the same resulting mech regardless of whether an earlier tenant already violated
 * (e.g. an over-SP loadout is still walked for a Flex-exclusivity violation). None of
 * the tenant bodies depend on an earlier tenant having passed (each reads off
 * `composedMounts`/`loadout.systems` directly), so this is safe to widen. Empty array
 * = legal. A frameless mech (no active loadout) is vacuously legal. Pure +
 * reference-only — no mutation, no I/O.
 *
 * `checkLoadoutInvariants` is exactly `loadoutViolations(...)[0] ?? null` — the two
 * are byte-identical on the first-violation question, so every existing first-only
 * pin (the three equip reducers' hard-reject wall, `loadoutPickLock.ts`) stays
 * correct unmodified. UI surfaces that want the *full* list (an editor should say
 * both problems, not just the first) call this instead.
 */
export function loadoutViolations(
  mech: MechData,
  pilot: PilotData,
  compendium: LoadoutInvariantCompendium,
): LoadoutViolation[] {
  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (!loadout) return [];

  const violations: LoadoutViolation[] = [];

  // ── SP tenant ──
  const spMax = computeSpMax(mech, pilot, compendium);
  const spUsed = computeSpUsed(mech, pilot, compendium);
  if (spUsed > spMax) {
    violations.push({
      kind: "sp",
      message: `SP used ${spUsed} would exceed budget ${spMax}; free up SP first`,
    });
  }

  // Gather the per-surface id lists once, off the COMPOSED array — a weapon/mod sitting
  // in a core-bonus bonus mount (e.g. Improved Armament's Flex mount) must be visible to
  // the Unique tenant just like a native-mount one.
  const weaponIds: string[] = [];
  const modIds: string[] = [];
  for (const mount of composedMounts(loadout, pilot, compendium)) {
    for (const slot of mount.slots) {
      if (slot.weaponId) weaponIds.push(slot.weaponId);
      if (slot.modId) modIds.push(slot.modId);
    }
  }
  const systemIds = loadout.systems ?? [];

  // ── Unique tenant (per-surface duplicate of a tg_unique id) ──
  const dupWeapon = firstUniqueDuplicate(weaponIds, "weapon", compendium);
  if (dupWeapon) {
    violations.push({ kind: "unique", message: `weapon ${dupWeapon} is Unique — a mech may install only one` });
  }
  const dupSystem = firstUniqueDuplicate(systemIds, "system", compendium);
  if (dupSystem) {
    violations.push({ kind: "unique", message: `system ${dupSystem} is Unique — a mech may install only one` });
  }
  const dupMod = firstUniqueDuplicate(modIds, "mod", compendium);
  if (dupMod) {
    violations.push({ kind: "unique", message: `mod ${dupMod} is Unique — a mech may install only one` });
  }

  // ── AI tenant (>1 system carrying tg_ai; systems-only) ──
  let aiCount = 0;
  for (const id of systemIds) {
    if (hasTag(compendium.getById("system", id), TG_AI)) aiCount += 1;
  }
  if (aiCount > 1) {
    violations.push({
      kind: "ai",
      message: `a mech may install only one AI system (found ${aiCount})`,
    });
  }

  // ── Superheavy bracing tenant: each Superheavy weapon claims one OTHER mount left
  //    empty as bracing (RAW core p.32). The shared bracing math lives in
  //    `superheavyBraceState`, which the UI also reads to mark a forced-brace mount. ──
  const brace = superheavyBraceState(mech, pilot, compendium);
  if (brace && brace.freeMounts < brace.required) {
    violations.push({
      kind: "superheavy",
      message: `a Superheavy weapon needs another mount left empty for bracing (need ${brace.required} spare mount${brace.required > 1 ? "s" : ""}, ${brace.freeMounts} free)`,
    });
  }

  // ── Flex mount exclusivity tenant: "either one MAIN weapon or up to two
  //    AUXILIARY weapons" (core p.32) — see the module doc-comment for why this
  //    needs its own tenant rather than living in `SLOT_FITTINGS`. ──
  const flexMessage = flexExclusivityViolation(mech, pilot, compendium);
  if (flexMessage) {
    violations.push({ kind: "flex", message: flexMessage });
  }

  return violations;
}

/**
 * Check the loadout invariants on a **resulting** mech, returning only the first
 * violation (tenant order `sp` → `unique` → `ai` → `superheavy` → `flex`) or `null`
 * when legal — the shape the three equip reducers' hard-reject wall and
 * `loadoutPickLock.ts` need. Exactly `loadoutViolations(...)[0] ?? null` (see that
 * function's doc comment for why widening to all-violations doesn't change this
 * answer). Pure + reference-only — no mutation, no I/O.
 */
export function checkLoadoutInvariants(
  mech: MechData,
  pilot: PilotData,
  compendium: LoadoutInvariantCompendium,
): LoadoutViolation | null {
  return loadoutViolations(mech, pilot, compendium)[0] ?? null;
}

/**
 * Whether `mount`'s slot 1 is Flex-locked: the mount presents as `"Flex"` (post
 * retrofit-in-place — a retrofitted mount presents as `"Main/Aux"` and is governed
 * by its own independent per-slot fittings instead, no cross-slot rule needed) AND
 * slot 0 holds a Main-sized weapon. RAW p.32: "either one MAIN weapon or up to two
 * AUXILIARY weapons" — a Main in slot 0 claims the whole mount, so slot 1 can hold
 * nothing else.
 *
 * The single source **both** the {@link flexExclusivityViolation} tenant below (a
 * violation is this being `true` while slot 1 is also occupied) **and** the editor
 * UI (which greys slot 1's affordance — greyed, never hidden) read — mirrors
 * {@link superheavyBraceState}'s split between the shared derivation and its tenant
 * caller. Takes a single already-composed `MountData` (a caller already walking
 * `composedMounts` passes one entry), not the whole mech/loadout.
 *
 * Pure + reference-only.
 */
export function flexSlot1Locked(
  mount: MountData,
  compendium: LoadoutInvariantCompendium,
): boolean {
  if (mount.type !== "Flex") return false;
  const slot0WeaponId = mount.slots[0]?.weaponId;
  if (!slot0WeaponId) return false;
  return compendium.getById("weapon", slot0WeaponId)?.mount === WEAPON_SIZE_MAIN;
}

/**
 * The Flex-exclusivity tenant's own check, factored out (mirrors
 * `superheavyBraceState` being its own function): walk the composed mounts and
 * reject any mount whose slot 1 holds a weapon while {@link flexSlot1Locked} says
 * that slot is locked. Returns a ready-to-surface message naming BOTH fixes, or
 * `null` when every Flex mount is legal.
 */
function flexExclusivityViolation(
  mech: MechData,
  pilot: PilotData,
  compendium: LoadoutInvariantCompendium,
): string | null {
  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (!loadout) return null;
  const composed = composedMounts(loadout, pilot, compendium);
  for (let mountIdx = 0; mountIdx < composed.length; mountIdx++) {
    const mount = composed[mountIdx]!;
    if (!mount.slots[1]?.weaponId) continue;
    if (!flexSlot1Locked(mount, compendium)) continue;
    return (
      `flex mount ${mountIdx}: a Main weapon and an Auxiliary weapon cannot share one ` +
      `Flexible mount (core p.32 — one Main weapon OR up to two Auxiliary); ` +
      `unequip the Main weapon, or unequip the second Auxiliary, to continue`
    );
  }
  return null;
}

/**
 * The Superheavy bracing status of a mech's active loadout, or `null` when no Superheavy
 * weapon is mounted. The single source of the bracing math, read by **both** the Superheavy
 * tenant above (a violation is `freeMounts < required`) **and** the editor UI (which marks a
 * mount the player can't fill).
 *
 * - `required` — Superheavy weapons mounted; each needs one OTHER mount left empty to brace.
 * - `freeMounts` — mounts that are *entirely* empty (the brace candidates). A partly-filled
 *   multi-slot mount is occupied, not free. In a legal loadout `freeMounts >= required`;
 *   **equality means no slack** — every empty mount is now a required brace, and equipping
 *   anything into one is exactly what the tenant would reject (so the UI shows it RESERVED
 *   instead of offering an equip).
 * - `superheavyIds` — the mounted Superheavy weapon ids (for naming the reason label).
 *
 * Walks `composedMounts(loadout, pilot, compendium)` rather than `loadout.mounts`
 * directly, so a Superheavy (or a would-be brace mount) sitting in a core-bonus bonus
 * mount is counted just like a native one — hence the required `pilot` parameter.
 *
 * **The Integrated Weapon bonus mount is excluded from the eligible-brace walk** — it can
 * never itself count as the "one other mount" a Superheavy braces against, even
 * when wholly empty. The Improved Armament Flex bonus mount stays counted. RAW
 * (core p.32) is silent on whether an integrated mount counts; the ruling rests on
 * the book's nearest signal, p.118's Improved Armament cap counting mounts
 * "excluding integrated mounts", and matches COMP/CON (`_ShLockDialog.vue`'s
 * `AllEquippableMounts` call, passed a false `integrated` argument). Discriminated
 * structurally off {@link composedMountProvenance}'s `storageField` — NOT
 * `mount.type === "Aux"` — so a homebrew LCP granting a differently-typed
 * integrated mount is still excluded.
 *
 * Pure + reference-only.
 */
export type SuperheavyBraceState = {
  required: number;
  freeMounts: number;
  superheavyIds: string[];
};

export function superheavyBraceState(
  mech: MechData,
  pilot: PilotData,
  compendium: LoadoutInvariantCompendium,
): SuperheavyBraceState | null {
  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (!loadout) return null;
  const superheavyIds: string[] = [];
  let freeMounts = 0;
  const composed = composedMounts(loadout, pilot, compendium);
  const provenance = composedMountProvenance(loadout, pilot, compendium);
  for (let mountIdx = 0; mountIdx < composed.length; mountIdx++) {
    const mount = composed[mountIdx]!;
    // The Integrated Weapon bonus mount never counts as a brace candidate,
    // even wholly empty — see the doc comment above. The Improved Armament Flex
    // bonus mount (a DIFFERENT `storageField`) is unaffected and stays counted.
    const isIntegratedBonusMount = provenance[mountIdx]?.storageField === "integratedWeaponMount";
    if (!isIntegratedBonusMount && mount.slots.every((s) => !s.weaponId)) freeMounts += 1;
    for (const slot of mount.slots) {
      if (slot.weaponId && compendium.getById("weapon", slot.weaponId)?.mount === WEAPON_SIZE_SUPERHEAVY) {
        superheavyIds.push(slot.weaponId);
      }
    }
  }
  if (superheavyIds.length === 0) return null;
  return { required: superheavyIds.length, freeMounts, superheavyIds };
}
